/**
 * Every player-facing MoveError message, in one place so the wording stays
 * consistent (curly quotes and apostrophes, correct singular/plural grammar,
 * squares named the way players read them — "H8").
 */
import { cellName } from './board';
import { MIN_BAG_FOR_EXCHANGE, RACK_SIZE } from './constants';
import type { Coord } from './types';

/** "A" · "A and B" · "A, B and C". */
export function joinList(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** Wrap a word in typographic quotes: QZX → “QZX”. */
export function quoteWord(word: string): string {
  return `“${word}”`;
}

function names(cells: readonly Coord[]): string {
  return joinList(cells.map((c) => cellName(c.row, c.col)));
}

export const messages = {
  noTiles: (): string => 'Place at least one tile on the board to play.',

  tooManyTiles: (placed: number): string =>
    `You can place at most ${RACK_SIZE} tiles in one turn (you placed ${placed}).`,

  duplicateTile: (): string => 'Each tile can only be placed once — the same tile appears twice in this move.',

  tileNotInRack: (count: number): string =>
    count === 1
      ? 'That tile isn’t on your rack.'
      : `${count} of those tiles aren’t on your rack.`,

  blankNeedsLetter: (at: Coord | null): string =>
    at ? `Choose a letter for the blank tile on ${cellName(at.row, at.col)}.` : 'Choose a letter for your blank tile.',

  invalidLetter: (at: Coord | null): string =>
    at
      ? `The blank tile on ${cellName(at.row, at.col)} must stand for a single letter from A to Z.`
      : 'A blank tile must stand for a single letter from A to Z.',

  outOfBounds: (count: number): string =>
    count === 1
      ? 'That square is off the board — every tile must go on the 15 × 15 grid.'
      : `${count} of those squares are off the board — every tile must go on the 15 × 15 grid.`,

  duplicateSquare: (cells: readonly Coord[]): string =>
    `Only one tile fits on a square — ${names(cells)} ${cells.length === 1 ? 'was' : 'were'} used twice.`,

  squareOccupied: (cells: readonly Coord[]): string =>
    cells.length === 1
      ? `${names(cells)} already has a tile on it.`
      : `${names(cells)} already have tiles on them.`,

  notInLine: (): string => 'Place all your tiles in a single row or column.',

  hasGaps: (gaps: readonly Coord[]): string =>
    `Your tiles must form one continuous word — fill the ${gaps.length === 1 ? 'gap' : 'gaps'} at ${names(gaps)}.`,

  firstMoveCenter: (): string => 'The first word must cover the ★ centre square.',

  firstMoveTooShort: (): string => 'The first word needs at least two letters.',

  notConnected: (): string => 'Your word must connect to tiles already on the board.',

  invalidWords: (words: readonly string[]): string =>
    `${joinList(words.map(quoteWord))} ${words.length === 1 ? 'isn’t' : 'aren’t'} in the dictionary.`,

  notYourTurn: (): string => 'It’s your opponent’s turn.',

  gameOver: (): string => 'This game is already over.',

  exchangeNone: (): string => 'Choose at least one tile to exchange.',

  exchangeTooMany: (count: number): string =>
    `You can exchange at most ${RACK_SIZE} tiles (you chose ${count}).`,

  bagTooSmall: (left: number): string =>
    `Exchanges need at least ${MIN_BAG_FOR_EXCHANGE} tiles in the bag (${left === 0 ? 'the bag is empty' : `${left} left`}).`,

  /** Used by exchange validation for its own tile-id checks. */
  exchangeDuplicate: (): string => 'Each tile can only be exchanged once — the same tile was chosen twice.',
} as const;
